import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Label } from '@shared/components/ui/shadcn/label';
import { useToast } from '@shared/hooks/use-toast';
import { AlertCircle, CheckCircle2, File, FileWarning, Loader2, Upload, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadCourierWeightFile } from '../../hooks';
import { MRW_COURIER_ID, type UploadCourierWeightFileResponse } from '../../models';

const ALLOWED_EXTENSIONS = ['.xlsx', '.xls'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

interface CourierWeightUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CourierWeightUploadDialog({ open, onOpenChange }: CourierWeightUploadDialogProps) {
  const { t } = useTranslation('dataLoadModule');
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadCourierWeightFileResponse | null>(null);

  const { uploadCourierWeightFileAsync, isUploading } = useUploadCourierWeightFile();

  const resetForm = () => {
    setFile(null);
    setDragActive(false);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const validateFile = (selectedFile?: File): boolean => {
    if (!selectedFile) {
      return false;
    }

    const extension = selectedFile.name.includes('.')
      ? selectedFile.name.slice(selectedFile.name.lastIndexOf('.')).toLowerCase()
      : '';

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      toast({
        title: t('upload.error.title', 'Error al subir archivo'),
        description: t('upload.error.invalid_type', 'Formato no permitido. Usa .xlsx o .xls.'),
        variant: 'destructive',
      });
      return false;
    }

    if (selectedFile.size > MAX_FILE_BYTES) {
      toast({
        title: t('upload.error.title', 'Error al subir archivo'),
        description: t('upload.error.too_large', 'El archivo supera el límite de 10 MB.'),
        variant: 'destructive',
      });
      return false;
    }

    setFile(selectedFile);
    return true;
  };

  const handleSubmit = async () => {
    if (!file) {
      toast({
        title: t('upload.error.title', 'Error al subir archivo'),
        description: t('upload.error.file_required', 'Debes seleccionar un archivo Excel.'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await uploadCourierWeightFileAsync({
        file,
        courier_id: MRW_COURIER_ID,
      });
      setUploadResult(result);
    } catch {
      /* toast en hook */
    }
  };

  const handleCloseAndReset = () => {
    onOpenChange(false);
    resetForm();
  };

  const handleClose = (nextOpen: boolean) => {
    if (!isUploading) {
      if (!nextOpen) {
        handleCloseAndReset();
      } else {
        onOpenChange(nextOpen);
      }
    }
  };

  const stats = uploadResult?.data;
  const totalSkipped = stats
    ? stats.skipped_invalid_status +
      stats.skipped_invalid_order_id +
      stats.skipped_invalid_weight +
      stats.skipped_order_not_found
    : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[520px]" data-testid="courier-weight-upload-dialog">
        {uploadResult && stats ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                {t('upload.result.title', 'Archivo procesado')}
              </DialogTitle>
              <DialogDescription>
                {uploadResult.message ||
                  t(
                    'upload.result.description',
                    'El archivo se ha procesado correctamente. A continuación se muestra el resumen:',
                  )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{t('upload.result.filename', 'Archivo')}: </span>
                {stats.filename}
              </p>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-muted/50 p-3 text-center">
                  <p className="text-2xl font-bold text-foreground">{stats.total_rows}</p>
                  <p className="text-xs text-muted-foreground">{t('upload.result.total_rows', 'Filas totales')}</p>
                </div>
                <div className="rounded-lg bg-muted/50 p-3 text-center">
                  <p className="text-2xl font-bold text-foreground">{stats.eligible_rows}</p>
                  <p className="text-xs text-muted-foreground">{t('upload.result.eligible_rows', 'Filas válidas')}</p>
                </div>
                <div className="rounded-lg bg-green-500/10 p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{stats.weights_saved}</p>
                  <p className="text-xs text-muted-foreground">{t('upload.result.weights_saved', 'Pesos guardados')}</p>
                </div>
              </div>

              {totalSkipped > 0 && (
                <div className="space-y-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
                  <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    {t('upload.result.skipped_title', 'Filas omitidas')} ({totalSkipped})
                  </p>
                  <div className="space-y-1 text-sm">
                    {stats.skipped_invalid_status > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <FileWarning className="h-3 w-3" />
                          {t('upload.result.skipped_invalid_status', 'Estado no válido:')}
                        </span>
                        <span className="font-medium text-yellow-600">{stats.skipped_invalid_status}</span>
                      </div>
                    )}
                    {stats.skipped_invalid_order_id > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <XCircle className="h-3 w-3" />
                          {t('upload.result.skipped_invalid_order_id', 'ID de pedido no válido:')}
                        </span>
                        <span className="font-medium text-yellow-600">{stats.skipped_invalid_order_id}</span>
                      </div>
                    )}
                    {stats.skipped_invalid_weight > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <XCircle className="h-3 w-3" />
                          {t('upload.result.skipped_invalid_weight', 'Peso no válido:')}
                        </span>
                        <span className="font-medium text-yellow-600">{stats.skipped_invalid_weight}</span>
                      </div>
                    )}
                    {stats.skipped_order_not_found > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <XCircle className="h-3 w-3" />
                          {t('upload.result.skipped_order_not_found', 'Pedido no encontrado:')}
                        </span>
                        <span className="font-medium text-yellow-600">{stats.skipped_order_not_found}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {stats.skipped_order_not_found_refs.length > 0 && (
                <div className="space-y-2 rounded-lg border border-border p-3">
                  <p className="text-sm font-medium text-foreground">
                    {t('upload.result.skipped_refs_title', 'Referencias sin pedido')}
                  </p>
                  <p className="text-sm text-muted-foreground break-words">
                    {stats.skipped_order_not_found_refs.join(', ')}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button onClick={handleCloseAndReset} data-testid="courier-weight-upload-result-close">
                {t('upload.result.close', 'Cerrar')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('upload.modal_title', 'Cargar pesos MRW')}</DialogTitle>
              <DialogDescription>
                {t(
                  'upload.modal_description',
                  'Sube el Excel de pesos de MRW. El archivo se procesará al instante y verás un resumen con las estadísticas de la importación.',
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                <p className="text-sm font-medium text-foreground">{t('upload.courier_mrw', 'Mensajería: MRW')}</p>
              </div>

              <div className="space-y-2">
                <Label>{t('upload.file', 'Archivo Excel')}</Label>
                <div
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setDragActive(false);
                    if (!isUploading) {
                      validateFile(event.dataTransfer.files?.[0]);
                    }
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!isUploading) {
                      setDragActive(true);
                    }
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setDragActive(false);
                  }}
                  className={[
                    'relative flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors',
                    dragActive
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-card hover:border-primary/50 hover:bg-accent/5',
                    isUploading ? 'cursor-not-allowed opacity-50' : '',
                  ].join(' ')}
                  data-testid="courier-weight-upload-dropzone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    disabled={isUploading}
                    onChange={(event) => validateFile(event.target.files?.[0])}
                    data-testid="courier-weight-upload-input"
                  />

                  {file ? (
                    <div className="flex flex-col items-center gap-2 px-4 text-center">
                      <File className="h-8 w-8 text-primary" />
                      <p className="text-sm font-medium text-foreground">
                        {t('upload.dropzone_selected', 'Archivo seleccionado')}
                      </p>
                      <p className="text-sm text-muted-foreground">{file.name}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 px-4 text-center">
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {t('upload.dropzone_empty', 'Arrastra un Excel aquí o haz clic para seleccionarlo')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('upload.format_hint', '.xlsx, .xls — máx. 10 MB')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={isUploading}>
                {t('upload.cancel', 'Cancelar')}
              </Button>
              <Button onClick={handleSubmit} disabled={isUploading || !file} data-testid="courier-weight-upload-submit">
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('upload.submitting', 'Procesando…')}
                  </>
                ) : (
                  t('upload.submit', 'Subir archivo')
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
